import React from 'react';
import SEO from '../components/SEO';
import Header from "../partials/header/Header";
import Footer from "../container/Footer/Footer";
import ScrollToTop from '../components/ScrollToTop.jsx';
import Accordion, {AccordionItem, AccordionTitle, AccordionContent} from "../components/Accordion";
import SwiperCore, {Pagination, Navigation} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

SwiperCore.use([Pagination, Navigation]);

const SUB_AGENCY_REGISTER_URL = "https://itools.igaruda.com/registerSubAgent.action";

const manageChecklist = [
    {icon: "fal fa-comments", desc: "Manage customer enquiries."},
    {icon: "fal fa-suitcase-rolling", desc: "Track every booking."},
    {icon: "fal fa-file-invoice", desc: "Generate quotations quickly."},
    {icon: "fal fa-credit-card", desc: "Monitor payments."},
    {icon: "fal fa-chart-line", desc: "View business reports."},
    {icon: "fal fa-cogs", desc: "Automate repetitive tasks."},
];

const capabilityBlocks = [
    {
        icon: "fal fa-user-friends",
        title: "Build Better Relationships with Every Customer",
        desc: "At Tetrahed, we make customer management simple. Our Travel Agency CRM and B2B Travel CRM keep customer details, bookings, and follow-ups organized, so you can spend less time searching for information and more time building lasting relationships.",
    },
    {
        icon: "fal fa-plane",
        title: "Make Every Flight Booking Effortless",
        desc: "With our B2B Flight Booking Portal, you can search, compare, and book flights in just a few clicks. Powered by a reliable Agency Booking Engine, we help you access competitive fares, manage booking changes with ease, and provide a seamless booking experience for every customer.",
    },
    {
        icon: "fal fa-th-large",
        title: "Bring Every Part of Your Business Together",
        desc: "At Tetrahed, we make it easy to manage every part of your business. Our Travel Agency Management Software keeps your customers, bookings, payments, and reports together, giving you complete visibility from a single dashboard. Access everything through one easy-to-use Travel Agent Portal designed for growing travel agencies.",
    },
    {
        icon: "fal fa-award",
        title: "Build Your Brand with a White Label Travel Solution",
        desc: "Create a professional booking experience under your own brand. Our White Label Travel Solution helps you strengthen your business identity, build customer trust, and expand your travel business with confidence.",
    },
];

const whyChooseCards = [
    {
        icon: "fal fa-clock",
        title: "Save Time Every Day",
        items: ["Automate repetitive tasks.", "Reduce manual work.", "Simplify daily workflows.", "Save valuable time.", "Focus on growing your business."],
    },
    {
        icon: "fal fa-tasks",
        title: "Improve Productivity Every Day",
        items: ["Help your team work more efficiently.", "Complete bookings in less time.", "Streamline everyday workflows.", "Reduce delays and bottlenecks.", "Get more done every day."],
    },
    {
        icon: "fal fa-smile",
        title: "Increase Customer Satisfaction",
        items: ["Respond to enquiries faster.", "Deliver personalized service.", "Resolve requests with ease.", "Keep customers informed.", "Build lasting customer relationships."],
    },
    {
        icon: "fal fa-chart-pie",
        title: "Make Better Business Decisions",
        items: ["Track business performance in real time.", "Understand booking and revenue trends.", "Generate actionable reports.", "Identify growth opportunities.", "Make confident, data-driven decisions."],
    },
];

const agencyTypes = [
    {icon: "fal fa-store-alt", title: "Offline Travel Agency"},
    {icon: "fal fa-shopping-bag", title: "Retail Travel Agency"},
    {icon: "fal fa-building", title: "Corporate Travel Agency"},
    {icon: "fal fa-rocket", title: "Startup Travel Agency"},
    {icon: "fal fa-home", title: "Home-Based Travel Agent"},
    {icon: "fal fa-sitemap", title: "Multi-Branch Travel Business"},
];

const howItWorks = [
    {icon: "fal fa-user-plus", desc: "Submit your Sub-Agency registration"},
    {icon: "fal fa-file-alt", desc: "Our team reviews your application"},
    {icon: "fal fa-check-circle", desc: "Complete onboarding and account setup"},
    {icon: "fal fa-rocket", desc: "Start booking and grow your travel business"},
];

const faqs = [
    {id: "one", q: "Why does my travel agency need a Travel Agency CRM?", a: "A Travel Agency CRM simplifies daily operations by bringing customer management, booking workflows, payment tracking, and reporting into one system. It helps agencies save time, improve customer service, and grow more efficiently."},
    {id: "two", q: "What features should I look for in a Travel Agency CRM?", a: "A reliable Travel Agency CRM should include customer management, quotation generation, flight booking, payment tracking, automated follow-ups, reporting, workflow automation, and role-based access."},
    {id: "three", q: "How does a B2B Travel CRM improve business operations?", a: "A B2B Travel CRM keeps all customer interactions, bookings, quotations, invoices, and payments organized in one place. It reduces manual processes, improves collaboration across teams, and enables faster response times for clients."},
    {id: "four", q: "Can I manage flight bookings through Tetrahed?", a: "Yes. Tetrahed includes a B2B Flight Booking Portal that allows travel agencies to search, book, and manage flight reservations from a single platform, making the booking process faster and more efficient."},
    {id: "five", q: "Does Tetrahed support multiple users and branch offices?", a: "Yes. Tetrahed is designed for growing travel businesses and supports multiple users and branch locations. Teams can securely access the platform based on their roles while managing operations from a centralized dashboard."},
    {id: "six", q: "Can I track customer enquiries and follow-ups?", a: "Absolutely. Tetrahed records customer enquiries, booking history, follow-up activities, and communication, helping your team respond quickly and build stronger customer relationships."},
    {id: "seven", q: "Does the Travel Agency Management Software generate reports?", a: "Yes. Tetrahed provides real-time business reports on bookings, payments, sales performance, and revenue trends, helping agency owners make informed business decisions."},
    {id: "eight", q: "Is Tetrahed suitable for small travel agencies?", a: "Yes. Whether you're an offline travel agency, startup, home-based travel agent, or an established travel business, Tetrahed is designed to simplify operations and support business growth."},
    {id: "nine", q: "Is my business data secure in Tetrahed?", a: "Yes. Tetrahed is built to keep customer information, booking records, and business data securely organized while providing authorized access to your team based on user roles."},
    {id: "ten", q: "How can Tetrahed help my travel agency grow?", a: "Tetrahed helps agencies grow by reducing manual work, improving customer management, streamlining bookings, providing actionable business insights, and enabling teams to operate more efficiently through one integrated Travel Agency CRM platform."},
];

const B2BTravelCRMSolutions = () => {
    return (
        <React.Fragment>
            <SEO
                title="Travel Agency CRM & B2B Booking Software | Tetrahed"
                description="Grow your travel business with Tetrahed's Travel Agency CRM. Manage bookings, customers, and daily operations with ease."
                URL="/products/b2b-travel-crm-solutions"
            />
            <Header />

            <div className="b2b-crm-page">
                {/* Hero */}
                <section className="b2b-crm-hero">
                    <div className="container">
                        <div className="row align-items-center">
                            <div className="col-lg-6" data-aos="fade-up">
                                <h1>Travel Agency CRM & B2B Booking Platform for <span>Growing</span> Travel Businesses</h1>
                                <p>Tetrahed is a complete Travel Agency CRM and B2B Travel Management Software that helps travel agencies manage customers, bookings, quotations, payments, suppliers, and reporting from one centralized platform. Whether you operate an offline travel agency, corporate travel business, or online booking portal, Tetrahed streamlines your entire workflow.</p>
                                <div className="b2b-crm-hero-buttons">
                                    <a className="btn btn-brand-color sub-agency-register-btn" href={SUB_AGENCY_REGISTER_URL} target="_blank" rel="noopener noreferrer">Register as a Sub-Agency <i className="fal fa-arrow-right"></i></a>
                                </div>
                                <div className="b2b-crm-trust-row">
                                    <div className="trust-item"><i className="fal fa-tags"></i> Competitive Fares</div>
                                    <div className="trust-item"><i className="fal fa-globe"></i> Global Inventory</div>
                                    <div className="trust-item"><i className="fal fa-headset"></i> Reliable Support</div>
                                    <div className="trust-item"><i className="fal fa-shield-check"></i> Secure & Trusted</div>
                                </div>
                            </div>
                            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="200">
                                <div className="b2b-crm-hero-visual">
                                    <div className="hero-dots-bg"></div>

                                    <div className="hero-laptop">
                                        <div className="hero-laptop-topbar">
                                            <span className="hero-dot dot-red"></span>
                                            <span className="hero-dot dot-yellow"></span>
                                            <span className="hero-dot dot-green"></span>
                                        </div>
                                        <div className="hero-laptop-body">
                                            <div className="hero-dash-sidebar">
                                                <div className="hero-dash-logo">TETRAHED</div>
                                                <ul>
                                                    <li className="active"><i className="fas fa-th-large"></i> Dashboard</li>
                                                    <li><i className="fal fa-calendar-check"></i> Bookings</li>
                                                    <li><i className="fal fa-users"></i> Customers</li>
                                                    <li><i className="fal fa-user-tie"></i> Agents</li>
                                                    <li><i className="fal fa-boxes"></i> Inventory</li>
                                                    <li><i className="fal fa-chart-bar"></i> Reports</li>
                                                    <li><i className="fal fa-percentage"></i> Commission</li>
                                                    <li><i className="fal fa-life-ring"></i> Support</li>
                                                    <li><i className="fal fa-cog"></i> Settings</li>
                                                </ul>
                                            </div>
                                            <div className="hero-dash-main">
                                                <div className="hero-dash-stats">
                                                    <div className="hero-dash-stat"><span>Total Bookings</span><strong>1,250</strong><em className="up">+12%</em></div>
                                                    <div className="hero-dash-stat"><span>Sales (USD)</span><strong>$98,765</strong><em className="up">+8%</em></div>
                                                    <div className="hero-dash-stat"><span>Commission (USD)</span><strong>$21,432</strong></div>
                                                    <div className="hero-dash-stat"><span>Active Customers</span><strong>2,350</strong></div>
                                                </div>
                                                <div className="hero-dash-row">
                                                    <div className="hero-dash-panel hero-dash-chart">
                                                        <div className="hero-dash-panel-title">Sales Overview <span>This Month</span></div>
                                                        <svg viewBox="0 0 220 70" preserveAspectRatio="none" className="hero-chart-svg">
                                                            <defs>
                                                                <linearGradient id="heroChartFill" x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor="#f15a24" stopOpacity="0.5" />
                                                                    <stop offset="100%" stopColor="#f15a24" stopOpacity="0" />
                                                                </linearGradient>
                                                            </defs>
                                                            <polygon points="0,55 28,38 56,50 84,18 112,42 140,14 168,32 196,20 220,10 220,70 0,70" fill="url(#heroChartFill)" />
                                                            <polyline points="0,55 28,38 56,50 84,18 112,42 140,14 168,32 196,20 220,10" fill="none" stroke="#f15a24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                    <div className="hero-dash-panel hero-dash-routes">
                                                        <div className="hero-dash-panel-title">Top Routes</div>
                                                        <ul>
                                                            <li><span>JFK &rarr; DEL</span><strong>$12,430</strong></li>
                                                            <li><span>EWR &rarr; BOM</span><strong>$9,215</strong></li>
                                                            <li><span>LAX &rarr; DEL</span><strong>$8,430</strong></li>
                                                            <li><span>ORD &rarr; HYD</span><strong>$7,130</strong></li>
                                                            <li><span>SFO &rarr; MAA</span><strong>$6,521</strong></li>
                                                        </ul>
                                                    </div>
                                                </div>
                                                <div className="hero-dash-panel hero-dash-table">
                                                    <div className="hero-dash-panel-title">Recent Bookings</div>
                                                    <table>
                                                        <thead>
                                                            <tr><th>PNR</th><th>Customer</th><th>Route</th><th>Date</th><th>Status</th></tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr><td>ABCD12</td><td>Ramesh Kumar</td><td>JFK &rarr; DEL</td><td>May 12, 2024</td><td><span className="hero-status-confirmed">Confirmed</span></td></tr>
                                                            <tr><td>EFGH34</td><td>Sunil Patel</td><td>EWR &rarr; BOM</td><td>May 12, 2024</td><td><span className="hero-status-confirmed">Confirmed</span></td></tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hero-phone">
                                        <div className="hero-phone-header">Search Flights</div>
                                        <div className="hero-phone-tabs">
                                            <span className="active">One Way</span><span>Round Trip</span><span>Multi City</span>
                                        </div>
                                        <div className="hero-phone-field"><span>From</span><strong>New York (JFK)</strong></div>
                                        <div className="hero-phone-field"><span>To</span><strong>New Delhi (DEL)</strong></div>
                                        <div className="hero-phone-field-row">
                                            <div className="hero-phone-field"><span>Date</span><strong>12 May, 2024</strong></div>
                                            <div className="hero-phone-field"><span>Travellers</span><strong>1 Adult</strong></div>
                                        </div>
                                        <div className="hero-phone-search-btn">Search Flights</div>
                                        <div className="hero-phone-help">
                                            <div className="hero-phone-help-avatar"><i className="fas fa-headset"></i></div>
                                            <div><strong>Need Help?</strong><span>Call our support<br/>(+1) 512 692 9119</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Simplify Every Part of Your Travel Business */}
                <section className="b2b-crm-features">
                    <div className="container">
                        <div className="b2b-section-title" data-aos="fade-up">
                            <h2>Simplify Every Part of Your <span>Travel Business</span></h2>
                            <p><strong>From Enquiries to Reports, Manage Everything in One Place.</strong><br/>Stop managing your business across multiple systems. At Tetrahed, we bring your daily operations into one centralized platform.</p>
                        </div>
                        <div className="row row-cols-lg-6 row-cols-md-3 row-cols-2">
                            {manageChecklist.map((item, key) => (
                                <div className="col mb-6" key={key} data-aos="fade-up" data-aos-delay={key * 100}>
                                    <div className="feature-card">
                                        <div className="feature-icon"><i className={item.icon}></i></div>
                                        <p>{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="b2b-crm-tagline" data-aos="fade-up">Spend less time on administration. Spend more time growing your business.</p>

                        <div className="d-none d-xl-block">
                            <div className="row row-cols-lg-2 row-cols-1 capability-row">
                                {capabilityBlocks.map((item, key) => (
                                    <div className="col mb-6" key={key} data-aos="fade-up" data-aos-delay={key * 100}>
                                        <div className="feature-card feature-card-wide">
                                            <div className="feature-card-head">
                                                <div className="feature-icon"><i className={item.icon}></i></div>
                                                <h3>{item.title}</h3>
                                            </div>
                                            <p>{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="d-xl-none">
                            <Swiper
                                className="capability-slider"
                                data-aos="fade-up"
                                spaceBetween={16}
                                slidesPerView={1.15}
                                pagination={{clickable: true}}
                                navigation
                                breakpoints={{
                                    768: {slidesPerView: 2, spaceBetween: 24},
                                }}
                            >
                                {capabilityBlocks.map((item, key) => (
                                    <SwiperSlide key={key}>
                                        <div className="feature-card feature-card-wide">
                                            <div className="feature-card-head">
                                                <div className="feature-icon"><i className={item.icon}></i></div>
                                                <h3>{item.title}</h3>
                                            </div>
                                            <p>{item.desc}</p>
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>
                </section>

                {/* Why Travel Agencies Choose Tetrahed */}
                <section className="b2b-crm-why-choose">
                    <div className="container">
                        <div className="b2b-section-title" data-aos="fade-up">
                            <h2>Why Travel Agencies Choose <span>Tetrahed</span></h2>
                        </div>
                        <Swiper
                            className="why-choose-slider"
                            data-aos="fade-up"
                            spaceBetween={16}
                            slidesPerView={1.15}
                            pagination={{clickable: true}}
                            navigation
                            breakpoints={{
                                1200: {slidesPerView: 4, spaceBetween: 24},
                                768: {slidesPerView: 2, spaceBetween: 24},
                            }}
                        >
                            {whyChooseCards.map((card, key) => (
                                <SwiperSlide key={key}>
                                    <div className="why-choose-card">
                                        <div className="why-choose-card-head">
                                            <div className="feature-icon"><i className={card.icon}></i></div>
                                            <h3>{card.title}</h3>
                                        </div>
                                        <ul>
                                            {card.items.map((line, idx) => (
                                                <li key={idx}><i className="fas fa-check-circle"></i> {line}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </section>

                {/* Built for Every Type of Travel Agency */}
                <section className="b2b-crm-benefits">
                    <div className="container">
                        <div className="b2b-section-title" data-aos="fade-up">
                            <h2>Built for Every Type of <span>Travel Agency</span></h2>
                            <p className="b2b-crm-tagline" data-aos="fade-up">Tetrahed helps you simplify operations, improve efficiency, and grow with confidence.</p>
                            <p>Whether you are a:</p>
                        </div>
                        <div className="benefits-grid">
                            {agencyTypes.map((item, key) => (
                                <div className="benefit-col" key={key} data-aos="fade-up" data-aos-delay={key * 80}>
                                    <div className="benefit-item">
                                        <div className="benefit-icon"><i className={item.icon}></i></div>
                                        <p>{item.title}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* How it works + CTA */}
                <section className="b2b-crm-how">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-6 mb-6 mb-lg-0" data-aos="fade-up">
                                <div className="how-steps-card">
                                    <h3>How It Works</h3>
                                    <div className="how-steps-row">
                                        {howItWorks.map((step, key) => (
                                            <React.Fragment key={key}>
                                                <div className={`how-step-col ${key === 0 || key === howItWorks.length - 1 ? "accent" : "neutral"}`}>
                                                    <div className="how-step-icon"><i className={step.icon}></i></div>
                                                    <h4>Step {key + 1}</h4>
                                                    <p>{step.desc}</p>
                                                </div>
                                                {key < howItWorks.length - 1 && <div className="how-step-connector"></div>}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="200">
                                <div className="how-cta">
                                    <div className="how-cta-map">
                                        <svg viewBox="0 0 500 220" className="cta-map-svg" preserveAspectRatio="xMidYMid slice">
                                            <defs>
                                                <pattern id="ctaMapDots" width="9" height="9" patternUnits="userSpaceOnUse">
                                                    <circle cx="1.2" cy="1.2" r="1.2" fill="rgba(255,255,255,0.16)" />
                                                </pattern>
                                            </defs>
                                            <rect width="500" height="220" fill="url(#ctaMapDots)" />
                                            <path d="M290,185 Q350,90 400,135 T475,90" fill="none" stroke="#f15a24" strokeWidth="1.5" strokeDasharray="4 4" />
                                            <circle cx="290" cy="185" r="5" fill="#f15a24" />
                                            <circle cx="400" cy="135" r="5" fill="#f15a24" />
                                            <circle cx="475" cy="90" r="5" fill="#f15a24" />
                                        </svg>
                                        <i className="fas fa-plane cta-plane-icon"></i>
                                    </div>
                                    <div className="how-cta-content">
                                        <h3>Take Your Travel Agency to the Next Level</h3>
                                        <p>Equip your agency with the right technology to manage bookings, customers, and business operations more efficiently. At Tetrahed, our Travel Agency CRM helps you stay organized, improve productivity, and grow your business.</p>
                                        <a className="btn btn-brand-color sub-agency-register-btn" href={SUB_AGENCY_REGISTER_URL} target="_blank" rel="noopener noreferrer">Get Started Today <i className="fal fa-arrow-right"></i></a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ + Contact */}
                <section className="b2b-crm-faq">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-7 mb-6 mb-lg-0" data-aos="fade-up">
                                <h3>Frequently Asked Questions</h3>
                                <div className="agency-accordion max-mb-n30">
                                    <Accordion>
                                        {faqs.map((item) => (
                                            <AccordionItem id={item.id} key={item.id}>
                                                <AccordionTitle id={item.id}>{item.q}</AccordionTitle>
                                                <AccordionContent id={item.id}>{item.a}</AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </div>
                            </div>
                            <div className="col-lg-5 faq-contact-sticky-col" data-aos="fade-up" data-aos-delay="200">
                                <div className="contact-help-card contact-help-card-floating">
                                    <h3>Have Questions? We're Here to Help!</h3>
                                    <p>Reach out to our team for more information about Tetrahed's Travel Agency CRM.</p>
                                    <ul>
                                        <li>
                                            <div className="contact-help-icon"><i className="fal fa-phone-alt"></i></div>
                                            <div><span>Call Us</span><a href="tel:+15126929119">(+1) 512 692 9119</a></div>
                                        </li>
                                        <li>
                                            <div className="contact-help-icon"><i className="fal fa-envelope"></i></div>
                                            <div><span>Email Us</span><a href="mailto:contactus@tetrahed.com">contactus@tetrahed.com</a></div>
                                        </li>
                                        <li>
                                            <div className="contact-help-icon"><i className="fal fa-globe"></i></div>
                                            <div><span>Visit Us</span><a href="https://www.tetrahed.com" target="_blank" rel="noopener noreferrer">www.tetrahed.com</a></div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
            <ScrollToTop />
        </React.Fragment>
    )
}

export default B2BTravelCRMSolutions;
